import api from '@/modules/api.service';
import { CandidatoDetalhesPdf } from './presentation/components/CandidatoDetalhesPDF';
import { ModalCorrigirDadosCandidato } from './presentation/components/ModalCorrigirDadosCandidato';
import { CandidatoService } from './candidato.service';
import { CursoService } from '@/modules/structure/curso/curso.service';
import { DataAmigavel, DataEHoraAmigavel } from '@/shared/utils/datas';
import { Endpoints } from '@/shared/constants/endpoints';
import { InstituicaoEnsinoService } from '@/modules/structure/instituicoes/instituicao.service';
import { ProcessoSeletivoService } from '../processo-seletivo/processo-seletivo.service';
import { ReadCandidatoDto } from '@/api.types';
import { exportFromJsxToPdf } from '@/shared/utils/pdf';
import {
  formatarContatoCandidato,
  formatarCpfExibicao,
  formatarEstadoCivilExibicao,
  formatarGeneroCandidato,
  formatarRacaCandidato,
  formatarSituacaoCandidato,
  getEnderecoExibicao,
  podeCorrigirDadosCandidato,
  podePromoverParaEstagiario,
  SITUACOES_EDITAVEIS_ADMIN,
  perguntaOptaVagasReservadas,
} from './candidatoDetalhes.utils';
import { mensagemErroRespostaApi } from '@/shared/utils/erros';
import { toast } from 'react-toastify';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { ValidarPermissaoUsuario } from '@/modules/system/auth/application/validate-permissions';
import { RoleEnum } from '@/modules/system/auth/domain/enums/role.enum';
import { PermissionEnum } from '@/modules/system/auth/domain/enums/permission.enum';
import { useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Form } from 'react-bootstrap';
import Links from '@/shared/constants/navegacao';
import MyContext from '@/shared/contexts/AppContext';

const candidatoService = new CandidatoService();
const processoSeletivoService = new ProcessoSeletivoService();
const cursoService = new CursoService();
const instituicaoEnsinoService = new InstituicaoEnsinoService();

function CardSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="p-0 mb-4">
      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">{title}</h5>
        </div>
        <div className="card-body">{children}</div>
      </div>
    </div>
  );
}

type FileAttachmentActionsProps = {
  fileId?: string | null;
  downloadFilename: string;
  onView: (id: string) => void;
  onDownload: (id: string, filename: string) => void;
  showView?: boolean;
};

function FileAttachmentActions({
  fileId,
  downloadFilename,
  onView,
  onDownload,
  showView = true,
}: FileAttachmentActionsProps) {
  if (!fileId) return <p className="text-muted mb-0">Nenhum arquivo anexado</p>;
  return (
    <div>
      <span className="d-none d-print-inline text-muted">Arquivo anexado</span>
      <span className="d-print-none">
        {showView && (
          <button type="button" className="btn btn-sm btn-primary me-2" onClick={() => onView(fileId)}>
            Visualizar
          </button>
        )}
        <button type="button" className="btn btn-sm btn-secondary" onClick={() => onDownload(fileId, downloadFilename)}>
          Baixar
        </button>
      </span>
    </div>
  );
}

export default function CandidatoDetalhesPage() {
  const navigate = useNavigate();
  const { setActions } = useBreadcrumbActions();
  const { authVisao } = useContext(MyContext)!;
  const params = useParams();
  const candidatoId = params.id ? Number(params.id) : 0;

  const [candidato, setCandidato] = useState<ReadCandidatoDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [processoSeletivo, setProcessoSeletivo] = useState<{ nome: string; provaUrl?: string | null } | null>(null);
  const [cursoPretendidoNomeExtra, setCursoPretendidoNomeExtra] = useState<string | null>(null);
  const [instituicaoRazaoSocialExtra, setInstituicaoRazaoSocialExtra] = useState<string | null>(null);
  const [savingSituacao, setSavingSituacao] = useState(false);
  const [showCorrigirDados, setShowCorrigirDados] = useState(false);

  const podeEditarSituacao = ValidarPermissaoUsuario(RoleEnum.ESTAGIARIO, PermissionEnum.UPDATE);
  const podeCorrigirDados = podeCorrigirDadosCandidato(authVisao);
  const exibirPromover =
    candidato != null &&
    podeEditarSituacao &&
    podePromoverParaEstagiario(candidato.situacao, candidato.pessoa);

  useEffect(() => {
    if (candidatoId <= 0) {
      setIsLoading(false);
      setCandidato(null);
      return;
    }
    let cancelled = false;

    const carregar = async () => {
      setIsLoading(true);
      setCandidato(null);
      setProcessoSeletivo(null);
      setCursoPretendidoNomeExtra(null);
      setInstituicaoRazaoSocialExtra(null);
      try {
        const result = await candidatoService.findOne(candidatoId);
        if (cancelled) return;
        setCandidato(result);

        const relacionados: Promise<unknown>[] = [];

        if (result.processoSeletivoId) {
          relacionados.push(
            processoSeletivoService
              .findOne(result.processoSeletivoId)
              .then((ps) => {
                if (!cancelled) setProcessoSeletivo({ nome: ps.nome, provaUrl: ps.provaUrl });
              })
              .catch(() => { }),
          );
        }
        if (result.cursoPretendidoId && !result.cursoPretendidoNome?.trim()) {
          relacionados.push(
            cursoService
              .findOne(result.cursoPretendidoId)
              .then((c) => {
                if (!cancelled) setCursoPretendidoNomeExtra(c.nome);
              })
              .catch(() => { }),
          );
        }
        if (result.instituicaoEnsinoId && !result.instituicaoEnsinoNome?.trim()) {
          relacionados.push(
            instituicaoEnsinoService
              .Read(result.instituicaoEnsinoId)
              .then((inst) => {
                if (!cancelled && inst) setInstituicaoRazaoSocialExtra(inst.razao_social);
              })
              .catch(() => { }),
          );
        }

        await Promise.all(relacionados);
      } catch (error) {
        if (!cancelled) {
          const msg = error instanceof Error ? error.message : String(error);
          toast.error('Falha ao carregar dados do candidato: ' + msg);
          navigate('/');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void carregar();
    return () => {
      cancelled = true;
    };
  }, [candidatoId, navigate]);

  const irParaPromoverWizard = useCallback(() => {
    navigate(`${Links.Candidato_Detalhes}/${candidatoId}/promover`);
  }, [candidatoId, navigate]);

  const abrirCorrigirDados = useCallback(() => {
    setShowCorrigirDados(true);
  }, []);

  useEffect(() => {
    if (!candidato) {
      setActions({});
      return;
    }
    setActions({
      ...(exibirPromover
        ? {
            primary: {
              label: 'Promover a estagiário',
              onClick: irParaPromoverWizard,
              variant: 'primary' as const,
            },
          }
        : {}),
      ...(podeCorrigirDados
        ? {
            secondary: {
              label: 'Corrigir dados',
              onClick: abrirCorrigirDados,
            },
          }
        : {}),
      print: {
        label: 'Imprimir',
        onClick: () =>
          void exportFromJsxToPdf(
            <CandidatoDetalhesPdf
              candidato={candidato}
              processoSeletivo={processoSeletivo}
              cursoPretendidoNomeExtra={cursoPretendidoNomeExtra}
              instituicaoRazaoSocialExtra={instituicaoRazaoSocialExtra}
            />,
          ),
      },
    });
    return () => setActions({});
  }, [
    candidato,
    processoSeletivo,
    cursoPretendidoNomeExtra,
    instituicaoRazaoSocialExtra,
    setActions,
    exibirPromover,
    irParaPromoverWizard,
    podeCorrigirDados,
    abrirCorrigirDados,
  ]);
  const handleAlterarSituacao = async (novaSituacao: string) => {
    if (!candidato || novaSituacao === candidato.situacao) return;
    setSavingSituacao(true);
    try {
      await candidatoService.update(candidatoId, { situacao: novaSituacao as ReadCandidatoDto['situacao'] });
      setCandidato({ ...candidato, situacao: novaSituacao as ReadCandidatoDto['situacao'] });
      toast.success('Situação atualizada.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : String(error));
    } finally {
      setSavingSituacao(false);
    }
  };

  const handleDownloadFile = useCallback(async (fileId: string, filename: string) => {
    try {
      const response = await api.get(Endpoints.CANDIDATO + '/file/' + fileId, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'arquivo.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(await mensagemErroRespostaApi(error, 'Erro ao baixar arquivo.'));
    }
  }, []);

  const handleViewFile = useCallback(async (fileId: string) => {
    try {
      const response = await api.get(Endpoints.CANDIDATO + '/file/' + fileId, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      toast.error(await mensagemErroRespostaApi(error, 'Erro ao abrir arquivo.'));
    }
  }, []);

  if (isLoading) {
    return (
      <div className="row">
        <div className="col-12 text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!candidato) {
    return (
      <div className="row">
        <div className="col-12">
          <p>Candidato não encontrado.</p>
        </div>
      </div>
    );
  }

  const enderecoExibicao = getEnderecoExibicao(candidato);

  return (
    <div className="row">
      <div className="col-md-8">
        <h3>Detalhes do Candidato</h3>
        {processoSeletivo && (
          <span className="blockquote-footer pb-4 text-end">
            Processo Seletivo: {processoSeletivo.nome}
          </span>
        )}
      </div>

      <hr className="pb-4" />

      <CardSection title="Identificação Pessoal">
        <div className="row g-3">

          <div className="col-12 col-sm-6 col-lg-2">
            <label className="form-label fw-bold mb-1 text-body-secondary small">Número do Candidato</label>
            <p className="mb-0">{candidato.id || '-'}</p>
          </div>
          <div className="col-12 col-sm-6 col-lg-2">
            <label className="form-label fw-bold mb-1 text-body-secondary small">CPF</label>
            <p className="mb-0">{formatarCpfExibicao(candidato.pessoa?.cpf)}</p>
          </div>
          <div className="col-12 col-sm-6 col-lg-3">
            <label className="form-label fw-bold mb-1 text-body-secondary small">Nome completo</label>
            <p className="mb-0 text-break">{candidato.pessoa?.nome || '-'}</p>
          </div>
          <div className="col-12 col-sm-6 col-lg-3">
            <label className="form-label fw-bold mb-1 text-body-secondary small">Nome social</label>
            <p className="mb-0 text-break">{candidato.nomeSocial?.trim() ? candidato.nomeSocial : '-'}</p>
          </div>

          <div className="col-sm-6 col-lg-2">
            <label className="form-label fw-bold mb-1 text-body-secondary small">Gênero</label>
            <p className="mb-0">{formatarGeneroCandidato(candidato.genero)}</p>
          </div>

          <div className="col-12 col-sm-6 col-lg-2">
            <label className="form-label fw-bold mb-1 text-body-secondary small">Estado civil</label>
            <p className="mb-0">{formatarEstadoCivilExibicao(candidato.pessoa?.estadocivil)}</p>
          </div>

          <div className="col-12 col-sm-6 col-lg-2">
            <label className="form-label fw-bold mb-1 text-body-secondary small">Data de Nascimento</label>
            <p className="mb-0">{DataAmigavel(candidato.pessoa?.dataNascimento) || '-'}</p>
          </div>

          <div className="col-md-2">
            <label className="form-label fw-bold mb-1 text-body-secondary small">Número de Contato</label>
            <p className="mb-0">{formatarContatoCandidato(candidato)}</p>
          </div>

          <div className="col-md-3">
            <label className="form-label fw-bold mb-1 text-body-secondary small">E-mail</label>
            <p className="mb-0 text-break">{candidato.pessoa?.email || '-'}</p>
            {podeCorrigirDados && (
              <Button
                variant="link"
                size="sm"
                className="px-0 mt-1 d-print-none"
                onClick={abrirCorrigirDados}
              >
                Corrigir e-mail
              </Button>
            )}
          </div>

          <div className="col-md-4">
            <label className="form-label fw-bold mb-2 text-body-secondary small">Documento de Identificação</label>
            <div className="mt-1">
              <FileAttachmentActions
                fileId={candidato.documentoIdentificacaoUrl}
                downloadFilename="documento_identificacao.pdf"
                onView={handleViewFile}
                onDownload={handleDownloadFile}
              />
            </div>
          </div>

        </div>
      </CardSection>

      <CardSection title="Dados Acadêmicos">
        <div className="row">
          <div className="mb-3 col-md-6">
            <label className="form-label fw-bold">Instituição de Ensino:</label>
            <p>{candidato.instituicaoEnsinoNome || instituicaoRazaoSocialExtra || '-'}</p>
          </div>

          <div className="mb-3 col-md-3">
            <label className="form-label fw-bold">Período Atual do Curso:</label>
            <p>{candidato.periodoAtualCurso ?? '-'}</p>
          </div>

          <div className="mb-3 col-md-3">
            <label className="form-label fw-bold">Ano de Início do Curso:</label>
            <p>{candidato.anoInicioCurso ?? '-'}</p>
          </div>

          <div className="mb-3 col-md-6">
            <label className="form-label fw-bold">Declaração de Matrícula:</label>
            <FileAttachmentActions
              fileId={candidato.documentoEstudoUrl}
              downloadFilename="documento_estudo.pdf"
              onView={handleViewFile}
              onDownload={handleDownloadFile}
            />
          </div>

          {candidato.provaUrl && (
            <div className="mb-3 col-md-6">
              <label className="form-label fw-bold">Prova enviada pelo candidato:</label>
              <FileAttachmentActions
                fileId={candidato.provaUrl}
                downloadFilename="prova.pdf"
                onView={handleViewFile}
                onDownload={handleDownloadFile}
                showView={false}
              />
            </div>
          )}
        </div>
      </CardSection>

      <CardSection title="Endereço">
        <div className="row">
          <div className="mb-3 col-md-2">
            <label className="form-label fw-bold">CEP:</label>
            <p>{enderecoExibicao?.cep?.trim() ? enderecoExibicao.cep : '-'}</p>
          </div>

          <div className="mb-3 col-md-2">
            <label className="form-label fw-bold">Número:</label>
            <p>{enderecoExibicao?.numero ?? '-'}</p>
          </div>

          <div className="mb-3 col-md-4">
            <label className="form-label fw-bold">Logradouro:</label>
            <p>{enderecoExibicao?.logradouro?.trim() ? enderecoExibicao.logradouro : '-'}</p>
          </div>

          <div className="mb-3 col-md-4">
            <label className="form-label fw-bold">Complemento:</label>
            <p>{enderecoExibicao?.complemento?.trim() ? enderecoExibicao.complemento : '-'}</p>
          </div>

          <div className="mb-3 col-md-2">
            <label className="form-label fw-bold">Estado:</label>
            <p>{enderecoExibicao?.estado?.trim() ? enderecoExibicao.estado : '-'}</p>
          </div>

          <div className="mb-3 col-md-4">
            <label className="form-label fw-bold">Cidade:</label>
            <p>{enderecoExibicao?.cidade?.trim() ? enderecoExibicao.cidade : '-'}</p>
          </div>

          <div className="mb-3 col-md-3">
            <label className="form-label fw-bold">Bairro:</label>
            <p>{enderecoExibicao?.bairro?.trim() ? enderecoExibicao.bairro : '-'}</p>
          </div>

          <div className="mb-3 col-md-4">
            <label className="form-label fw-bold">Comprovante de Residência:</label>
            <FileAttachmentActions
              fileId={candidato.comprovanteResidenciaUrl}
              downloadFilename="comprovante_residencia.pdf"
              onView={handleViewFile}
              onDownload={handleDownloadFile}
            />
          </div>
        </div>
      </CardSection>

      <CardSection title="Curso e Município de Interesse">
        <div className="row">
          <div className="mb-3 col-md-6">
            <label className="form-label fw-bold">Curso Pretendido:</label>
            <p>{candidato.cursoPretendidoNome || cursoPretendidoNomeExtra || '-'}</p>
          </div>

          <div className="mb-3 col-md-6">
            <label className="form-label fw-bold">Município:</label>
            <p>{candidato.municipio || '-'}</p>
          </div>
        </div>
      </CardSection>

      <CardSection title="Identificação Étnico-Racial (Ações Afirmativas)">
        <div className="row">
          <div className="mb-3 col-md-6">
            <label className="form-label fw-bold">Identificação Racial:</label>
            <p>{formatarRacaCandidato(candidato.raca)}</p>
          </div>

          <div className="mb-3 col-md-6">
            <label className="form-label fw-bold">{perguntaOptaVagasReservadas(candidato.raca)}</label>
            <p>{candidato.optaVagasNegros ? 'Sim' : 'Não'}</p>
          </div>

          <div className="mb-3 col-md-6">
            <label className="form-label fw-bold">Declara-se indígena?</label>
            <p>{candidato.declaraIndigena ? 'Sim' : 'Não'}</p>
          </div>

          {candidato.documentacaoIndigenaUrl && (
            <div className="mb-3 col-md-6">
              <label className="form-label fw-bold">Documentação Comprobatória Indígena:</label>
              <FileAttachmentActions
                fileId={candidato.documentacaoIndigenaUrl}
                downloadFilename="documentacao_indigena.pdf"
                onView={handleViewFile}
                onDownload={handleDownloadFile}
              />
            </div>
          )}
        </div>
      </CardSection>

      <CardSection title="Pessoa com Deficiência (PcD)">
        <div className="row">
          <div className="mb-3 col-md-6">
            <label className="form-label fw-bold">Opta por concorrer às vagas reservadas para pessoa com deficiência?</label>
            <p>{candidato.optaVagasPcd ? 'Sim' : 'Não'}</p>
          </div>

          {candidato.optaVagasPcd && (
            <>
              <div className="mb-3 col-md-6">
                <label className="form-label fw-bold">Descrição da deficiência:</label>
                <p>{candidato.descricaoDeficiencia || '-'}</p>
              </div>

              <div className="mb-3 col-md-6">
                <label className="form-label fw-bold">Especifique sua deficiência:</label>
                <p>{candidato.especificacaoDeficiencia || '-'}</p>
              </div>

              <div className="mb-3 col-md-6">
                <label className="form-label fw-bold">Necessita de cuidados especiais para realização da prova?</label>
                <p>{candidato.necessitaCuidadosEspeciais ? 'Sim' : 'Não'}</p>
              </div>
            </>
          )}

          {candidato.laudoMedicoUrl && (
            <div className="mb-3 col-md-6">
              <label className="form-label fw-bold">Laudo Médico:</label>
              <FileAttachmentActions
                fileId={candidato.laudoMedicoUrl}
                downloadFilename="laudo_medico.pdf"
                onView={handleViewFile}
                onDownload={handleDownloadFile}
              />
            </div>
          )}
        </div>
      </CardSection>

      {candidato.documentosComplementaresUrl && (
        <CardSection title="Documentos Opcionais">
          <div className="row">
            <div className="mb-3 col-md-12">
              <label className="form-label fw-bold">Documentos Complementares Escolar:</label>
              <FileAttachmentActions
                fileId={candidato.documentosComplementaresUrl}
                downloadFilename="documentos_complementares.pdf"
                onView={handleViewFile}
                onDownload={handleDownloadFile}
              />
            </div>
          </div>
        </CardSection>
      )}

      <CardSection title="Declarações">
        <div className="row">
          <div className="mb-3 col-md-12">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                checked={candidato.declaracaoVeracidade}
                disabled
              />
              <label className="form-check-label">
                Declaração de veracidade das informações prestadas
              </label>
            </div>
          </div>

          <div className="mb-3 col-md-12">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                checked={candidato.confirmacaoEdital}
                disabled
              />
              <label className="form-check-label">
                Confirmação de ciência e concordância com o edital do processo seletivo
              </label>
            </div>
          </div>

          <div className="mb-3 col-md-12">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                checked={candidato.consentimentoLgpd}
                disabled
              />
              <label className="form-check-label">
                Consentimento para tratamento de dados pessoais, nos termos da Lei Geral de Proteção de Dados (LGPD)
              </label>
            </div>
          </div>
        </div>
      </CardSection>

      <CardSection title="Informações do Sistema">
        <div className="row">
          <div className="mb-3 col-md-4">
            <label className="form-label fw-bold">Situação:</label>
            {podeEditarSituacao && candidato.situacao !== 'rascunho' && candidato.situacao !== 'matriculado' ? (
              <Form.Select
                value={candidato.situacao}
                disabled={savingSituacao}
                onChange={(ev) => void handleAlterarSituacao(ev.target.value)}
                aria-label="Alterar situação da inscrição"
              >
                {SITUACOES_EDITAVEIS_ADMIN.map((s) => (
                  <option key={s} value={s}>
                    {formatarSituacaoCandidato(s)}
                  </option>
                ))}
              </Form.Select>
            ) : (
              <p className="mb-0">{formatarSituacaoCandidato(candidato.situacao)}</p>
            )}
          </div>

          <div className="mb-3 col-md-4">
            <label className="form-label fw-bold">Data de Inscrição:</label>
            <p className="mb-0">{DataEHoraAmigavel(candidato.dataInscricao) || '-'}</p>
          </div>

          <div className="mb-3 col-md-4">
            <label className="form-label fw-bold">Última Atualização:</label>
            <p className="mb-0">{DataEHoraAmigavel(candidato.editadoEm) || '-'}</p>
          </div>
        </div>

        {exibirPromover && (
          <div className="mt-2 pt-3 border-top">
            <p className="text-muted small mb-2">
              Candidato convocado com cadastro de pessoa materializado. Inicie o fluxo guiado para promover, revisar o cadastro e lotar o estagiário.
            </p>
            <Button variant="primary" onClick={irParaPromoverWizard}>
              Promover a estagiário
            </Button>
          </div>
        )}

        {podeEditarSituacao && candidato.situacao !== 'convocada' && candidato.situacao !== 'matriculado' && candidato.situacao !== 'rascunho' && (
          <p className="text-muted small mb-0 mt-3">
            Para promover a estagiário, altere a situação para <strong>Convocada</strong>.
          </p>
        )}
      </CardSection>

      {podeCorrigirDados && (
        <ModalCorrigirDadosCandidato
          show={showCorrigirDados}
          candidatoId={candidatoId}
          emailAtual={candidato.pessoa?.email}
          onHide={() => setShowCorrigirDados(false)}
          onSuccess={(novoEmail) => {
            setCandidato((atual) => {
              if (!atual) return atual;
              return {
                ...atual,
                pessoa: atual.pessoa
                  ? { ...atual.pessoa, email: novoEmail }
                  : ({ email: novoEmail } as ReadCandidatoDto['pessoa']),
              };
            });
          }}
        />
      )}
    </div>
  );
}
